import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { useActingOrg } from '@/auth/actingOrgContext';
import {
    fetchAdminApps,
    fetchAppRoles,
    fetchRoleKeys,
    fetchRoleMaps,
    fetchRoleMapScopeDiff,
    fetchRoleMapSyncStatus,
} from '@/api/console';
import { fetchOrgLevels } from '@/api/orgAdmin';
import { useAccountAssignments } from './useAccountCounts';
import { useRoleMapView } from './useRoleMapView';
import { useRoleMapWrites } from './useRoleMapWrites';
import { accountCountsByRoleKey, computeScopeOverrideSummaries, hasActiveFilters, type RoleMapFilterState } from './helpers';
import {
    DEFAULT_VISIBLE_COLUMNS,
    ROLE_MAP_COLUMNS,
    standardRolesFromRoleKeys,
    type RoleMapColumnKey,
    type RoleMapGroup,
    type RoleMapScope,
    type RoleMapSortDirection,
    type RoleMapSortField,
    type RoleMapStatusFilter,
} from './types';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * EAA-1837: view model behind `/console/role-map` — the "Liên kết vai trò"
 * table. Everything stateful lives here
 * or in the two hooks it composes (`useRoleMapView` = pure derivation,
 * `useRoleMapWrites` = every mutation), so `RoleMapPage.tsx` stays a layout
 * shell. The three-way split mirrors `accounts/useAccountsScan` +
 * `useAccountsView`, the app's other bulk-select table.
 *
 * Two things carried over unchanged from the EAA-1817 coverage board because
 * they were, and remain, correct: the capability model
 * (`console.rolemap.view` gates the route, `console.rolemap.manage` means "may
 * edit the Mặc định scope", `org:{id}.manage` gates the org's own override) and
 * the acting-org account-count walk (`useAccountCounts`).
 *
 * ONE HARD BACKEND CONSTRAINT shapes this whole screen. Every EAA-1837 bulk
 * endpoint is `/organizations/{org_id}/role-map/...` and reads through
 * `RoleMapService.list_for_organization`, which deliberately EXCLUDES
 * ecosystem-wide defaults (`organization_id IS NULL`); `bulk_set_scope` and
 * `bulk_deactivate` additionally filter `organization_id=org_id`. There is
 * therefore NO way to bulk-scope / bulk-resync / bulk-delete / export a default
 * row — such ids come straight back in `skipped`. So while the "Phạm vi"
 * dropdown sits on "Mặc định", the bulk bar, the row checkboxes and the CSV
 * export are all disabled with an explicit reason rather than firing requests
 * that are guaranteed to no-op. See `bulkDisabledReason` below.
 */
export function useRoleMapTable({ active = true }: { active?: boolean } = {}) {
    // EAA-1845: `useTranslation` is a hook, not a component — calling it here
    // is fine even though this file has no JSX. `bulkDisabledReason` below is
    // read straight into the DOM by `RoleMapListView`, so it is user-facing
    // text like anything else in this batch.
    const { t } = useTranslation();
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const acting = useActingOrg();

    const actingOrgId = acting?.actingOrgId ?? null;
    const actingOrgName = acting?.actingOrg?.name ?? null;
    // EAA-1817: `active` is no longer used to gate this hook — `RoleMapPage`
    // passes `true` on every view, because frames 55/55a put "Xuất CSV" and
    // "Thêm liên kết" in the title strip of ALL of them and both are driven from
    // here. The param stays as the seam for gating the queries (hooks cannot be
    // called conditionally), and un-gating costs almost nothing: the keys this
    // shares with the board (`admin-apps`, `role-maps`, `role-keys`) are
    // identical, so the overlap resolves from one cache entry.
    const enabled = active && authenticated && Boolean(accessToken) && can('console.rolemap.view');
    const canManageDefault = enabled && can('console.rolemap.manage');
    const canManageOrg = enabled && Boolean(actingOrgId) && can(`org:${actingOrgId}.manage`);
    const canSync = enabled && can('console.rolemap.sync');

    /* ---------------- queries ---------------- */
    const appsQuery = useQuery({
        queryKey: ['admin-apps', accessToken],
        queryFn: () => fetchAdminApps(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const roleMapsQuery = useQuery({
        queryKey: ['role-maps', accessToken],
        queryFn: () => fetchRoleMaps(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const syncStatusQuery = useQuery({
        queryKey: ['role-map-sync-status', accessToken],
        queryFn: () => fetchRoleMapSyncStatus(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const levelsQuery = useQuery({
        queryKey: ['org-levels', accessToken, actingOrgId],
        queryFn: () => fetchOrgLevels(config.apiBase, accessToken as string, actingOrgId as string),
        enabled: enabled && Boolean(actingOrgId),
        retry: false,
    });
    // EAA-1817: the governed role-key vocabulary (`GET /api/v1/role-keys`) —
    // replaces the old Levels-only derivation for `standardRoles` below. See
    // `standardRolesFromRoleKeys`'s docstring for why the source changed and
    // what it falls back to when this comes back empty.
    const roleKeysQuery = useQuery({
        queryKey: ['role-keys', accessToken],
        queryFn: () => fetchRoleKeys(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const assignmentsQuery = useAccountAssignments(
        config.apiBase,
        accessToken,
        actingOrgId,
        enabled
    );

    const apps = useMemo(() => appsQuery.data ?? [], [appsQuery.data]);
    const rows = useMemo(() => roleMapsQuery.data ?? [], [roleMapsQuery.data]);
    // EAA-1817 (frame 59): org-scope +/- diff summary derived from the same
    // cached role-map payload — no whole-list scope-diff endpoint exists.
    const scopeOverrideSummaries = useMemo(
        () => computeScopeOverrideSummaries(rows, actingOrgId, apps),
        [rows, actingOrgId, apps]
    );
    const levels = useMemo(() => levelsQuery.data?.results ?? [], [levelsQuery.data]);
    const standardRoles = useMemo(
        () => standardRolesFromRoleKeys(roleKeysQuery.data ?? [], rows),
        [roleKeysQuery.data, rows]
    );
    const accountCounts = useMemo(
        () => accountCountsByRoleKey(assignmentsQuery.data ?? [], levels),
        [assignmentsQuery.data, levels]
    );

    /* ---------------- scope ---------------- */
    const [scope, setScopeState] = useState<RoleMapScope>(canManageDefault ? 'default' : 'org');
    useEffect(() => {
        // EAA-1817, carried over: an operator with no `console.rolemap.manage`
        // never gets the Mặc định slice — force back to 'org' if capabilities
        // change under them (e.g. an acting-org switch while mounted).
        if (!canManageDefault && scope === 'default') setScopeState('org');
    }, [canManageDefault, scope]);

    /* ---------------- filters / sort / paging / columns ---------------- */
    const [search, setSearchState] = useState('');
    const [statusFilter, setStatusFilter] = useState<RoleMapStatusFilter[]>([]);
    const [appFilter, setAppFilter] = useState<string[]>([]);
    const [sortField, setSortField] = useState<RoleMapSortField>('role_key');
    const [sortDirection, setSortDirection] = useState<RoleMapSortDirection>('asc');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSizeState] = useState(PAGE_SIZE_OPTIONS[0]);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [visibleColumns, setVisibleColumns] =
        useState<RoleMapColumnKey[]>(DEFAULT_VISIBLE_COLUMNS);
    /* EAA-1817: `searchInputRef`/`focusSearch` and the `openFilter` menu-state
       pair used to live here. Both existed only to let a column header act as a
       SECOND trigger for the hand-written toolbar's popovers — the search icon
       focused the toolbar input, the funnel icons opened its menus. The kit
       `DataTable` puts the filter dropdown ON the header cell (`ColumnConfig.filter`)
       and owns the search box, so there is no second trigger to coordinate and
       nothing left to hold open. */

    const filters: RoleMapFilterState = useMemo(
        () => ({ search, status: statusFilter, apps: appFilter }),
        [search, statusFilter, appFilter]
    );
    const filtersActive = hasActiveFilters(filters);

    const view = useRoleMapView(
        rows,
        apps,
        accountCounts,
        scope,
        actingOrgId,
        filters,
        sortField,
        sortDirection,
        page,
        pageSize
    );

    /* ---------------- selection (per VISUAL row = per group) ---------------- */
    // EAA-1837: the checkbox selects a GROUP KEY, and every bulk call expands the
    // selected keys back into EVERY underlying RoleMap id. Selecting the
    // `teacher_lead`/LMS row therefore acts on both `teacher` and `admin_class`
    // — see `RoleMapGroup`'s docstring for the EAA-1828 bug class this avoids.
    const { visibleGroups, pageGroups } = view;
    const selectedGroups = useMemo(
        () => visibleGroups.filter((group) => selectedKeys.has(group.key)),
        [visibleGroups, selectedKeys]
    );
    const selectedIds = useMemo(
        () => selectedGroups.flatMap((group) => group.ids),
        [selectedGroups]
    );

    // Prune the selection whenever the visible set changes (a bulk delete removes
    // rows; a filter change hides them) so "Đã chọn N dòng" can never count a row
    // the operator can no longer see or act on.
    useEffect(() => {
        setSelectedKeys((prev) => {
            if (prev.size === 0) return prev;
            const visible = new Set(visibleGroups.map((group) => group.key));
            let changed = false;
            const next = new Set<string>();
            for (const key of prev) {
                if (visible.has(key)) next.add(key);
                else changed = true;
            }
            return changed ? next : prev;
        });
    }, [visibleGroups]);

    const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);

    /**
     * EAA-1884: replaces the old `toggleRow`/`toggleAll`/`allSelected`/
     * `someSelected`/`pageKeys` quintet now that `RoleMapTable` wires the
     * kit `DataTable`'s native `rowSelection` (antd `Table` underneath)
     * instead of hand-rolled checkboxes. antd's own selection hook is
     * CONTROLLED the same way this Set already was: it calls back with the
     * full next key list (previous `selectedRowKeys` plus/minus whatever the
     * operator just toggled on the CURRENTLY RENDERED page), and — because
     * every disabled row is excluded from its own "select all" bookkeeping —
     * reproduces `allSelected`/`someSelected`'s exact page-scoped math and
     * the header checkbox's auto-disable-when-every-row-disabled behaviour
     * for free (verified against antd's `useSelection` hook). No page-only
     * derivation is needed here any more; antd already has it.
     */
    const onSelectionChange = useCallback((keys: (string | number)[]) => {
        setSelectedKeys(new Set(keys.map(String)));
    }, []);

    /* ---------------- toolbar setters (each resets paging) ---------------- */
    const setSearch = useCallback((value: string) => {
        setSearchState(value);
        setPage(1);
    }, []);

    const toggleStatus = useCallback((value: RoleMapStatusFilter) => {
        setStatusFilter((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
        setPage(1);
    }, []);

    const toggleApp = useCallback((code: string) => {
        setAppFilter((prev) =>
            prev.includes(code) ? prev.filter((v) => v !== code) : [...prev, code]
        );
        setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        setSearchState('');
        setStatusFilter([]);
        setAppFilter([]);
        setPage(1);
    }, []);

    const setScope = useCallback(
        (next: RoleMapScope) => {
            setScopeState(next);
            setPage(1);
            // A selection made in one scope is meaningless in the other (the two
            // slices share no rows), and carrying it over would let a Mặc định
            // selection leak into an org-scoped bulk call.
            clearSelection();
        },
        [clearSelection]
    );

    const setPageSize = useCallback((size: number) => {
        setPageSizeState(size);
        setPage(1);
    }, []);

    /**
     * EAA-1817: the kit `DataTable` toolbar's controlled contract — one
     * `filterValues` bag in, one `(key, value)` callback out.
     *
     * WHY THIS ADAPTER EXISTS RATHER THAN THE TOOLBAR CALLING `toggleStatus`.
     * `DataTable`'s filter controls are SET-style (a multi-select hands back the
     * whole next array; a select hands back the whole next value), while this
     * hook's own API is toggle-style because the hand-written toolbar it
     * replaced was a list of checkboxes. Converting here — once, next to the
     * `setPage(1)` every filter change owes — keeps the paging reset in one
     * place instead of asking each control to remember it. The toggles stay
     * exported: the row-level menus still toggle one value at a time.
     */
    const filterValues = useMemo(
        () => ({ status: statusFilter, application: appFilter, scope }),
        [statusFilter, appFilter, scope]
    );

    const setFilterValue = useCallback(
        (key: string, value: unknown) => {
            if (key === 'status') {
                setStatusFilter((value as RoleMapStatusFilter[] | null) ?? []);
                setPage(1);
                return;
            }
            if (key === 'application') {
                setAppFilter((value as string[] | null) ?? []);
                setPage(1);
                return;
            }
            if (key === 'scope') {
                // `setScope` also clears the selection — a selection made in one
                // slice is meaningless in the other. Guard the no-op so merely
                // clearing the control does not wipe a live selection.
                const next = (value as RoleMapScope | null) ?? 'default';
                if (next !== scope) setScope(next);
            }
        },
        [scope, setScope]
    );

    /**
     * EAA-1817: set the sort outright. The kit `DataTable` reports a whole
     * next-state (an ordering string), not "the user clicked this header", so
     * the toggle below cannot serve it — a toggle would invert antd's own
     * already-applied direction on every click. `toggleSort` stays for the
     * places that really are a single header click.
     */
    const setSort = useCallback((field: RoleMapSortField, direction: RoleMapSortDirection) => {
        setSortField(field);
        setSortDirection(direction);
    }, []);

    const toggleSort = useCallback(
        (field: RoleMapSortField) => {
            if (field === sortField) {
                setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                return;
            }
            setSortField(field);
            setSortDirection('asc');
        },
        [sortField]
    );

    /* ---------------- column visibility ---------------- */
    const isColumnVisible = useCallback(
        (key: RoleMapColumnKey) => visibleColumns.includes(key),
        [visibleColumns]
    );
    const toggleColumn = useCallback((key: RoleMapColumnKey) => {
        const spec = ROLE_MAP_COLUMNS.find((column) => column.key === key);
        if (spec?.pinned) return;
        setVisibleColumns((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    }, []);
    const resetColumns = useCallback(() => setVisibleColumns(DEFAULT_VISIBLE_COLUMNS), []);
    /**
     * EAA-1817: the kit toolbar's column picker is also set-style (it hands
     * back the whole next visible-key list). Pinned columns are re-added here
     * rather than trusted from the caller: `role_key` is the row's identity and
     * a table without it is unreadable, which is why `toggleColumn` refuses to
     * remove it too.
     */
    const setVisibleColumnKeys = useCallback((keys: string[]) => {
        const pinned = ROLE_MAP_COLUMNS.filter((column) => column.pinned).map(
            (column) => column.key
        );
        const next = keys.filter((key): key is RoleMapColumnKey =>
            ROLE_MAP_COLUMNS.some((column) => column.key === key)
        );
        setVisibleColumns([...new Set([...pinned, ...next])] as RoleMapColumnKey[]);
    }, []);

    /* ---------------- bulk gating ---------------- */
    const bulkEnabled = scope === 'org' && Boolean(actingOrgId) && canManageOrg;
    const canManageCurrentScope = scope === 'default' ? canManageDefault : canManageOrg;

    /**
     * EAA-1837: why a bulk action is unavailable, in the operator's words; `null`
     * when it IS available. The Mặc định case is a real backend limitation stated
     * plainly rather than papered over — see this hook's docstring.
     */
    let bulkDisabledReason: string | null = null;
    if (scope === 'default') {
        bulkDisabledReason = t('console.roleMap.bulkDisabledReason.scopeDefault');
    } else if (!actingOrgId) {
        bulkDisabledReason = t('console.roleMap.bulkDisabledReason.noOrg');
    } else if (!canManageOrg) {
        bulkDisabledReason = t('console.roleMap.bulkDisabledReason.noPermission');
    }

    /* ---------------- writes ---------------- */
    const writes = useRoleMapWrites({
        apiBase: config.apiBase,
        token: accessToken,
        actingOrgId,
        scope,
        canManageDefault,
        canManageOrg,
        clearSelection,
    });

    const applyBulkScope = useCallback(
        (target: 'default' | 'org') => {
            if (!bulkEnabled || !actingOrgId || selectedIds.length === 0) return;
            writes.setScopeFor({
                ids: selectedIds,
                organization_id: target === 'default' ? null : actingOrgId,
            });
        },
        [bulkEnabled, actingOrgId, selectedIds, writes]
    );

    const applyBulkResync = useCallback(() => {
        if (!bulkEnabled || selectedIds.length === 0) return;
        writes.resyncIds(selectedIds);
    }, [bulkEnabled, selectedIds, writes]);

    const applyBulkDelete = useCallback(() => {
        if (!bulkEnabled || selectedIds.length === 0) return;
        writes.deleteIds(selectedIds);
    }, [bulkEnabled, selectedIds, writes]);

    const resyncGroup = useCallback(
        (group: RoleMapGroup) => {
            if (!bulkEnabled) return;
            writes.resyncIds(group.ids);
        },
        [bulkEnabled, writes]
    );

    const setGroupScope = useCallback(
        (group: RoleMapGroup, target: 'default' | 'org') => {
            if (!bulkEnabled || !actingOrgId) return;
            writes.setScopeFor({
                ids: group.ids,
                organization_id: target === 'default' ? null : actingOrgId,
            });
        },
        [bulkEnabled, actingOrgId, writes]
    );

    /** EAA-1837: the export endpoint runs through the same org-scoped
     *  `list_for_organization` queryset as the bulk actions, so it can only
     *  export the acting org's own override rows — disabled in the Mặc định
     *  slice for exactly that reason, never silently exporting the wrong set. */
    const exportEnabled = scope === 'org' && Boolean(actingOrgId);
    /**
     * EAA-1817 (frame 55c): the export takes the scope the popover chose.
     *
     * `appCode` narrows to one application by resolving it to that app's row ids
     * — the endpoint filters by `ids`, it has no app param. A selection still
     * wins over both: if the operator ticked rows, those rows ARE the scope, and
     * quietly widening the file to the whole app would export more than was
     * asked for.
     */
    const exportCsv = useCallback(
        (options?: { appCode?: string | null; includeCodes?: boolean }) => {
            if (!exportEnabled) return Promise.resolve();
            const includeCodes = options?.includeCodes ?? false;
            if (selectedIds.length > 0) return writes.exportCsv(selectedIds, includeCodes);
            const appCode = options?.appCode;
            if (appCode) {
                const ids = visibleGroups
                    .filter((group) => group.application === appCode)
                    .flatMap((group) => group.ids);
                return writes.exportCsv(ids, includeCodes);
            }
            return writes.exportCsv([], includeCodes);
        },
        [exportEnabled, writes, selectedIds, visibleGroups]
    );

    /* ---------------- re-point ("Đổi vai trò") modal ---------------- */
    // EAA-1817: `PATCH /role-maps/{id}` is gated on `ROLEMAP_MANAGE` via
    // `@require_permissions_for_method`, not a superadmin check (see
    // `useRoleMapWrites.repointMutation`'s docstring) — which is what
    // `canManageDefault` carries, so the trigger's gate matches the server. Restrict
    // it to single-id groups only (an unambiguous target) and let the row action gate
    // visibility on `canManageDefault`.
    const [repointTarget, setRepointTarget] = useState<RoleMapGroup | null>(null);
    const openRepoint = useCallback(
        (group: RoleMapGroup) => {
            writes.repointMutation.reset();
            setRepointTarget(group);
        },
        [writes]
    );
    const closeRepoint = useCallback(() => setRepointTarget(null), []);

    // Same catalog `fetchAppRoles` the Add-link picker uses — a re-point can
    // only land on a role the target application currently declares.
    const repointAppRolesQuery = useQuery({
        queryKey: ['admin-app-roles', repointTarget?.application, accessToken],
        queryFn: () =>
            fetchAppRoles(config.apiBase, accessToken as string, repointTarget!.application),
        enabled: enabled && Boolean(repointTarget) && canManageDefault,
        retry: false,
    });

    /* ---------------- scope-diff ("Xem khác biệt với mặc định") panel ---------------- */
    // EAA-1817: per-row diff against the ecosystem default (frame 59), scoped
    // to whichever group's row action opened it — not the whole-list summary
    // the design export shows, which has no single-pair endpoint behind it
    // (`fetchRoleMapScopeDiff` takes exactly one (application, role_key) pair).
    const [scopeDiffTarget, setScopeDiffTarget] = useState<RoleMapGroup | null>(null);
    const openScopeDiff = useCallback((group: RoleMapGroup) => setScopeDiffTarget(group), []);
    const closeScopeDiff = useCallback(() => setScopeDiffTarget(null), []);

    const scopeDiffQuery = useQuery({
        queryKey: [
            'role-map-scope-diff',
            accessToken,
            scopeDiffTarget?.application,
            scopeDiffTarget?.role_key,
            actingOrgId,
        ],
        queryFn: () =>
            fetchRoleMapScopeDiff(config.apiBase, accessToken as string, {
                application: scopeDiffTarget!.application,
                roleKey: scopeDiffTarget!.role_key,
                organization: actingOrgId as string,
            }),
        enabled: enabled && Boolean(scopeDiffTarget) && Boolean(actingOrgId) && scope === 'org',
        retry: false,
    });

    const dropOverride = useCallback(() => {
        if (!scopeDiffTarget || !actingOrgId) return;
        writes.dropOverrideMutation.mutate(
            {
                application: scopeDiffTarget.application,
                roleKey: scopeDiffTarget.role_key,
                organization: actingOrgId,
            },
            { onSuccess: () => setScopeDiffTarget(null) }
        );
    }, [scopeDiffTarget, actingOrgId, writes]);

    /* ---------------- add-link modal ---------------- */
    const [addOpen, setAddOpen] = useState(false);
    const [addApp, setAddApp] = useState<string | null>(null);
    const openAdd = useCallback(() => setAddOpen(true), []);
    const closeAdd = useCallback(() => setAddOpen(false), []);

    /**
     * EAA-1841: the picker now asks for the acting org's OWN catalog rows too
     * when the modal is writing an org-scoped link. Two reasons, both concrete:
     *
     * `GET /admin/app-roles` defaults to ecosystem-wide rows only
     * (`organization__isnull=True` server-side) and `organization={uuid}` is
     * what adds an org's custom rows — the param exists precisely so an org
     * override can link them. And every role created through
     * `POST /admin/app-roles` is upserted ORG-SCOPED, so without this the
     * operator would create a role here and watch it never appear in the list
     * they created it for.
     *
     * Omitted in the default scope on purpose: a default-scope link must not be
     * able to point at a role that exists in one org only.
     */
    const addAppRolesOrg = scope === 'org' ? actingOrgId : null;
    const addAppRolesQuery = useQuery({
        queryKey: ['admin-app-roles', addApp, addAppRolesOrg, accessToken],
        queryFn: () =>
            fetchAppRoles(config.apiBase, accessToken as string, addApp as string, {
                ...(addAppRolesOrg ? { organization: addAppRolesOrg } : {}),
            }),
        enabled: enabled && addOpen && Boolean(addApp),
        retry: false,
    });

    // EAA-1817: the "create app role from inside the Add-link modal" escape
    // hatch (`roleCreateApp`/`openRoleCreate`/`closeRoleCreate`) is REMOVED,
    // not hidden — this phase manages system/ecosystem-default app roles
    // only, and that flow always wrote an ORG-SCOPED `AppRole`. See
    // `RoleMapAddModal.tsx`'s `canSubmit` comment for the full reasoning.
    const { createLinks: runCreate } = writes;
    const createLinks = useCallback(
        async (input: { role_key: string; application: string; appRoles: string[] }) => {
            await runCreate(input);
            setAddOpen(false);
        },
        [runCreate]
    );

    const loading =
        roleMapsQuery.isLoading ||
        appsQuery.isLoading ||
        roleKeysQuery.isLoading ||
        (Boolean(actingOrgId) && levelsQuery.isLoading);

    return {
        enabled,
        /** EAA-1841: the capability gate ALONE, unaffected by `active` — the
         *  merged page decides whether to render the route from this, while
         *  `enabled` additionally means "this view is the one on screen". */
        canView: authenticated && Boolean(accessToken) && can('console.rolemap.view'),
        loading,
        isError: roleMapsQuery.isError || appsQuery.isError,

        canManageDefault,
        canManageOrg,
        canManageCurrentScope,
        canSync,
        actingOrgId,
        actingOrgName,

        apps,
        standardRoles,
        syncStatusQuery,
        syncMutation: writes.syncMutation,

        totalLinks: view.scopedRows.length,
        appCount: view.appCount,

        search,
        setSearch,
        statusFilter,
        toggleStatus,
        appFilter,
        toggleApp,
        clearFilters,
        filtersActive,

        scope,
        setScope,
        scopeOverrideSummaries,

        visibleColumns,
        isColumnVisible,
        toggleColumn,
        resetColumns,
        setVisibleColumnKeys,
        filterValues,
        setFilterValue,

        pageGroups,
        filteredCount: visibleGroups.length,
        /** EAA-1817: every group surviving the filters. The Add-link modal reads
         *  the chosen role's account count off it (frame 55b's consequence
         *  line) instead of issuing a second request for a number already here. */
        visibleGroups,
        sortField,
        sortDirection,
        toggleSort,
        setSort,

        selectedKeys,
        selectedGroups,
        selectedCount: selectedGroups.length,
        selectedIdCount: selectedIds.length,
        onSelectionChange,
        clearSelection,

        bulkEnabled,
        bulkDisabledReason,
        bulkBusy: writes.busy,
        applyBulkScope,
        applyBulkResync,
        applyBulkDelete,
        removeGroup: writes.removeGroup,
        resyncGroup,
        setGroupScope,

        exportCsv,
        exportBusy: writes.exportBusy,
        exportEnabled,

        repointTarget,
        openRepoint,
        closeRepoint,
        repointMutation: writes.repointMutation,
        repointAppRoles: repointAppRolesQuery.data ?? [],
        repointAppRolesLoading: repointAppRolesQuery.isLoading,

        scopeDiffTarget,
        openScopeDiff,
        closeScopeDiff,
        scopeDiffQuery,
        dropOverride,
        dropOverrideBusy: writes.dropOverrideMutation.isPending,

        undo: writes.undo,

        addOpen,
        openAdd,
        closeAdd,
        addApp,
        setAddApp,
        addAppRoles: addAppRolesQuery.data ?? [],
        addAppRolesLoading: addAppRolesQuery.isLoading,
        createLinks,
        addBusy: writes.createBusy,
        createMutation: writes.createMutation,

        page: view.page,
        setPage,
        pageSize,
        setPageSize,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        totalPages: view.totalPages,
    };
}

export type RoleMapTableVM = ReturnType<typeof useRoleMapTable>;
